<!-- @format -->

<template>
	<disabled-text v-if="disabled && pcProps.showDisabledText" :value="disabledTextValue" />
	<template v-else>
		<template v-if="pcProps.isTimeRange">
			<el-date-picker
				v-model="value"
				:type="typeItem.type === 'time' ? 'datetimerange' : typeItem.type + 'range'"
				:format="typeItem.format"
				:value-format="pcProps.isTimestamp ? 'x' : typeItem.format"
				:disabled="disabled"
				unlink-panels
				v-bind="$attrs"
			/>
		</template>
		<template v-else>
			<template v-if="typeItem.type === 'time'">
				<el-time-picker v-model="value" :format="typeItem.format" :disabled="disabled" v-bind="$attrs" />
			</template>

			<template v-else>
				<el-date-picker
					v-model="value"
					:type="typeItem.type"
					:format="typeItem.format"
					:value-format="pcProps.isTimestamp ? 'x' : typeItem.format"
					:disabled="disabled"
					unlink-panels
					v-bind="$attrs"
				/>
			</template>
		</template>
	</template>
</template>

<script setup lang="ts">
	import dayjs from 'dayjs';
	import { timeTypeList } from '../../config';
	import { type WidgetConfigType } from '../../types';
	import DisabledText from './disabledText.vue';

	interface Props {
		modelValue: string | number;
		pcProps: WidgetConfigType;
		disabled?: boolean;
		item?: any;
	}
	const props = withDefaults(defineProps<Props>(), {
		modelValue: '',
	});

	const splitFlag = computed(() => (props.pcProps.splitFlag ? props.pcProps.splitFlag : ','));

	const emit = defineEmits(['update:modelValue', 'onClick']);

	const typeItem = computed(() => timeTypeList.find((itm) => itm.format === props.pcProps.timeType) || timeTypeList[0]);
	const disabledTextValue = computed(() => {
		if (!value.value) return '';

		const isTimeType = typeItem.value.type === 'time';
		const formatValue = (val: any) =>
			isTimeType
				? (val && dayjs(val).format(typeItem.value.format)) || ''
				: (val && dayjs(val?.toString()).format(typeItem.value.format)) || '';

		if (props.pcProps.isTimeRange) {
			return `${formatValue(value.value[0])}--${formatValue(value.value[1])}`;
		}

		return formatValue(value.value);
	});
	const getTimestampValue = (data: string | number, isTenTimestamp?: boolean): number => {
		const num = Number(data);
		return isTenTimestamp ? num * 1000 : num;
	};

	const getValue = (data: string | number): any => {
		if (!data) return '';

		if (props.pcProps.isTimestamp) {
			return getTimestampValue(data, props.pcProps.isTenTimestamp);
		}

		if (typeItem.value.type === 'time') {
			return dayjs(data, typeItem.value.format) || '';
		}

		return data;
	};

	const setValue = (val: any): string | number => {
		if (!val) return '';

		if (props.pcProps.isTimestamp) {
			return props.pcProps.isTenTimestamp ? dayjs(val).unix() : dayjs(val).valueOf();
		}

		if (typeItem.value.type === 'time') {
			return dayjs(val).format(typeItem.value.format) || '';
		}

		return val;
	};

	const value = computed({
		get() {
			if (props.pcProps.isTimeRange) {
				if (!props.modelValue) return ['', ''];
				const [start, end] = String(props.modelValue).split(splitFlag.value);
				return [getValue(start), getValue(end)];
			}
			return getValue(props.modelValue);
		},
		set(val) {
			if (props.pcProps.isTimeRange) {
				const newValue = val?.[0] && val?.[1] ? `${setValue(val[0])}${splitFlag.value}${setValue(val[1])}` : '';
				emit('update:modelValue', newValue);
				return;
			}
			emit('update:modelValue', setValue(val));
		},
	});
</script>

<style scoped lang="scss"></style>
